import AlertBanner from "components/AlertBanner";
import FirmwareDeployModal from "components/modals/FirmwareDeployModal";
import FirmwareUploadModal from "components/modals/FirmwareUploadModal";
import { useDevices } from "hooks/useDevices";
import { getGreenhouseDevices } from "services/greenhouseApi";
import { describeOtaFailure, parseDeployTarget } from "services/firmwareTargets";
import {
  AlertCircle,
  CheckCircle,
  Database,
  Download,
  Edit2,
  Loader2,
  RefreshCw,
  Save,
  TrendingUp,
  Upload,
  Users,
  Wifi,
  WifiOff,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  deployFirmware,
  deployGreenhouseFirmware,
  getFirmwareDeployments,
  getFirmwareStats,
  getFirmwareVersions,
  getSystemOverview,
  updateFirmwareVersion,
  uploadFirmware,
} from "services/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const changelogMarkdownComponents = {
  h1: ({ children, ...props }) => (
    <h3 className="text-base font-semibold text-on-surface mt-3 mb-1" {...props}>
      {children}
    </h3>
  ),
  h2: ({ children, ...props }) => (
    <h3 className="text-base font-semibold text-on-surface mt-3 mb-1" {...props}>
      {children}
    </h3>
  ),
  h3: ({ children, ...props }) => (
    <h4 className="text-sm font-semibold text-on-surface mt-2 mb-1" {...props}>
      {children}
    </h4>
  ),
  h4: ({ children, ...props }) => (
    <h5 className="text-sm font-semibold text-on-surface mt-2 mb-1" {...props}>
      {children}
    </h5>
  ),
  p: ({ children, ...props }) => (
    <p className="text-sm text-on-surface-variant my-1" {...props}>
      {children}
    </p>
  ),
  ul: ({ children, ...props }) => (
    <ul className="list-disc pl-5 text-sm text-on-surface-variant my-1 space-y-0.5" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="list-decimal pl-5 text-sm text-on-surface-variant my-1 space-y-0.5" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li className="text-sm" {...props}>{children}</li>
  ),
  code: ({ children, ...props }) => (
    <code className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono" {...props}>
      {children}
    </code>
  ),
  pre: ({ children, ...props }) => (
    <pre className="bg-gray-100 rounded p-2 my-2 overflow-x-auto text-xs [&>code]:block [&>code]:bg-transparent [&>code]:p-0" {...props}>
      {children}
    </pre>
  ),
  strong: ({ children, ...props }) => (
    <strong className="font-semibold text-on-surface" {...props}>{children}</strong>
  ),
  a: ({ children, ...props }) => (
    <a className="text-blue-600 hover:underline" target="_blank" rel="noreferrer" {...props}>
      {children}
    </a>
  ),
};

const formatFileSize = (bytes) => {
  if (!bytes) return "N/A";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

// Firmware library and OTA deployments, for the whole fleet. A device's own settings are on its
// Detail page (Configuration tab).
const FirmwarePage = () => {
  const { devices } = useDevices();

  const [deployments, setDeployments] = useState([]);
  const [firmwareVersions, setFirmwareVersions] = useState([]);
  const [greenhouseControllers, setGreenhouseControllers] = useState([]);
  const [firmwareStats, setFirmwareStats] = useState(null);
  const [systemOverview, setSystemOverview] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [activeTab, setActiveTab] = useState("firmware");

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    version: "",
    description: "",
    is_stable: false,
    is_latest: false,
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Inline changelog edit state
  const [editingFirmwareId, setEditingFirmwareId] = useState(null);
  const [editingDescription, setEditingDescription] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);

  const startEditDescription = (firmware) => {
    setEditingFirmwareId(firmware.id);
    setEditingDescription(firmware.description || "");
  };
  const cancelEditDescription = () => {
    setEditingFirmwareId(null);
    setEditingDescription("");
  };
  const saveDescription = async (firmwareId) => {
    try {
      setSavingDescription(true);
      await updateFirmwareVersion(firmwareId, { description: editingDescription });
      setFirmwareVersions((prev) =>
        prev.map((f) =>
          f.id === firmwareId ? { ...f, description: editingDescription } : f,
        ),
      );
      setSuccess("Changelog updated");
      cancelEditDescription();
    } catch (err) {
      setError(`Failed to update changelog: ${err.message}`);
    } finally {
      setSavingDescription(false);
    }
  };

  // Deploy modal state
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [selectedDeviceForDeploy, setSelectedDeviceForDeploy] = useState("");
  const [selectedFirmwareId, setSelectedFirmwareId] = useState("");
  const [deploying, setDeploying] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [deviceFilter, setDeviceFilter] = useState("");

  const fetchSystemOverviewData = async () => {
    try {
      const data = await getSystemOverview();
      setSystemOverview(data);
    } catch (err) {
      console.error("Error fetching system overview:", err);
    }
  };

  const fetchFirmwareVersions = async () => {
    try {
      const data = await getFirmwareVersions();
      setFirmwareVersions(data.firmwares || []);
    } catch (err) {
      console.error("Error fetching firmware versions:", err);
    }
  };

  const fetchFirmwareStatsData = async () => {
    try {
      const data = await getFirmwareStats();
      setFirmwareStats(data.statistics);
    } catch (err) {
      console.error("Error fetching firmware stats:", err);
    }
  };

  const fetchDeployments = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (deviceFilter) params.append("device", deviceFilter);
      params.append("limit", "100");
      const data = await getFirmwareDeployments(params.toString());
      setDeployments(data.deployments || []);
    } catch (err) {
      console.error("Error fetching deployments:", err);
    }
  };

  const fetchGreenhouseControllers = async () => {
    try {
      const data = await getGreenhouseDevices();
      setGreenhouseControllers(data.devices || []);
    } catch (err) {
      console.error("Error fetching greenhouse controllers:", err);
    }
  };

  const handleUploadFirmware = async () => {
    if (!uploadFile || !uploadForm.version) {
      setError("Please provide both firmware file and version");
      return;
    }
    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append("firmware_file", uploadFile);
      formData.append("version", uploadForm.version);
      formData.append("description", uploadForm.description);
      formData.append("is_stable", uploadForm.is_stable);
      formData.append("is_latest", uploadForm.is_latest);

      await uploadFirmware(formData);
      setSuccess(`Firmware v${uploadForm.version} uploaded successfully!`);
      setShowUploadModal(false);
      setUploadForm({
        version: "",
        description: "",
        is_stable: false,
        is_latest: false,
      });
      setUploadFile(null);
      await fetchFirmwareVersions();
      await fetchFirmwareStatsData();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeployFirmware = async () => {
    if (!selectedDeviceForDeploy || !selectedFirmwareId) {
      setError("Please select both device and firmware version");
      return;
    }
    setDeploying(true);
    setError(null);
    setSuccess(null);

    try {
      const target = parseDeployTarget(selectedDeviceForDeploy);
      const body = { firmware_id: selectedFirmwareId };
      if (target.kind === "greenhouse") {
        await deployGreenhouseFirmware(target.mac, body);
      } else {
        await deployFirmware(target.mac, body);
      }
      setSuccess(`Firmware deployment initiated for ${target.mac}`);
      setShowDeployModal(false);
      setSelectedDeviceForDeploy("");
      setSelectedFirmwareId("");
      await fetchDeployments();
      await fetchSystemOverviewData();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeploying(false);
    }
  };

  useEffect(() => {
    fetchSystemOverviewData();
    fetchFirmwareVersions();
    fetchFirmwareStatsData();
    fetchDeployments();
    fetchGreenhouseControllers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchDeployments();
  }, [statusFilter, deviceFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // While any deployment is still in flight, refresh the list so pending ->
  // downloading -> success shows up without a manual reload.
  const hasDeploymentInFlight = deployments.some((d) =>
    ["pending", "downloading", "installing"].includes(d.status),
  );
  useEffect(() => {
    if (!hasDeploymentInFlight) return undefined;
    const interval = setInterval(() => {
      fetchDeployments();
      fetchGreenhouseControllers();
    }, 5000);
    return () => clearInterval(interval);
  }, [hasDeploymentInFlight, statusFilter, deviceFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "online":
      case "success":
        return "bg-green-100 text-green-800";
      case "offline":
      case "failed":
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      case "warning":
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "critical":
        return "bg-red-100 text-red-800";
      case "downloading":
      case "installing":
        return "bg-blue-100 text-blue-800";
      case "never-connected":
        return "bg-gray-100 text-on-surface-variant";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "online":
      case "success":
        return <CheckCircle className="w-4 h-4" />;
      case "offline":
      case "never-connected":
      case "failed":
        return <WifiOff className="w-4 h-4" />;
      case "downloading":
      case "installing":
        return <Loader2 className="w-4 h-4 animate-spin" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <>
      <FirmwareUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        form={uploadForm}
        onFormChange={setUploadForm}
        file={uploadFile}
        onFileChange={setUploadFile}
        onUpload={handleUploadFirmware}
        uploading={uploading}
      />

      <FirmwareDeployModal
        isOpen={showDeployModal}
        onClose={() => setShowDeployModal(false)}
        devices={devices}
        greenhouseControllers={greenhouseControllers}
        firmwareVersions={firmwareVersions}
        selectedDevice={selectedDeviceForDeploy}
        onDeviceChange={setSelectedDeviceForDeploy}
        selectedFirmwareId={selectedFirmwareId}
        onFirmwareChange={setSelectedFirmwareId}
        onDeploy={handleDeployFirmware}
        deploying={deploying}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold font-headline text-on-surface tracking-tight">
              Firmware
            </h1>
            <p className="text-on-surface-variant mt-1">
              Upload firmware builds and deploy them over the air
            </p>
          </div>
        </div>

        <AlertBanner
          type="error"
          message={error}
          onDismiss={() => setError(null)}
        />
        <AlertBanner
          type="success"
          message={success}
          onDismiss={() => setSuccess(null)}
        />

        {/* System Overview Cards */}
        {systemOverview && firmwareStats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              {
                icon: Users,
                value: systemOverview.devices.total,
                label: "Total Devices",
                color: "text-blue-600",
              },
              {
                icon: Wifi,
                value: systemOverview.devices.online_1h,
                label: "Online (1h)",
                color: "text-green-600",
              },
              {
                icon: Database,
                value: firmwareStats.versions.total,
                label: "FW Versions",
                color: "text-purple-600",
              },
              {
                icon: CheckCircle,
                value: systemOverview.firmware_deployments.successful,
                label: "Successful",
                color: "text-green-600",
              },
              {
                icon: TrendingUp,
                value: firmwareStats.deployments.success_rate
                  ? `${firmwareStats.deployments.success_rate.toFixed(1)}%`
                  : "N/A",
                label: "Success Rate",
                color: "text-blue-600",
              },
            ].map(({ icon: Icon, value, label, color }) => (
              <div
                key={label}
                className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 p-4 sm:p-6"
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-7 h-7 ${color}`} />
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-on-surface">
                      {value}
                    </p>
                    <p className="text-xs sm:text-sm text-on-surface-variant">
                      {label}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {[
              { key: "firmware", label: "Firmware Library", Icon: Database },
              { key: "deployments", label: "Deployments", Icon: Zap },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-shrink-0 px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === key
                    ? "border-primary text-primary"
                    : "border-transparent text-on-surface-variant hover:text-on-surface"
                }`}
              >
                <Icon className="w-4 h-4 inline mr-1 sm:mr-2" />
                {label}
              </button>
            ))}
          </div>

          <div className="p-4 sm:p-6">
            {/* Firmware Library Tab */}
            {activeTab === "firmware" && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <h3 className="text-lg font-semibold text-on-surface">
                    Firmware Library
                  </h3>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="px-4 py-2 bg-primary text-on-primary rounded-full hover:bg-primary-container flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Upload
                    </button>
                    <button
                      onClick={fetchFirmwareVersions}
                      className="px-4 py-2 text-on-surface-variant border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Refresh
                    </button>
                  </div>
                </div>

                {firmwareVersions.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Database className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">
                      No firmware versions yet
                    </p>
                    <p className="text-sm mt-2">
                      Upload your first firmware to get started
                    </p>
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="mt-4 px-4 py-2 bg-primary text-on-primary rounded-full hover:bg-primary-container inline-flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Upload Firmware
                    </button>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {firmwareVersions.map((firmware) => (
                      <div
                        key={firmware.id}
                        className="border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <h4 className="text-lg font-semibold text-on-surface">
                                Version {firmware.version}
                              </h4>
                              {firmware.is_latest && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                                  Latest
                                </span>
                              )}
                              {firmware.is_stable && (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                                  Stable
                                </span>
                              )}
                              {firmware.product === "greenhouse-controller" && (
                                <span
                                  className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded"
                                  data-testid="product-badge"
                                >
                                  Greenhouse controller
                                </span>
                              )}
                              {!firmware.is_active && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded">
                                  Inactive
                                </span>
                              )}
                            </div>
                            {editingFirmwareId === firmware.id ? (
                              <div className="mb-3">
                                <textarea
                                  value={editingDescription}
                                  onChange={(e) =>
                                    setEditingDescription(e.target.value)
                                  }
                                  rows={10}
                                  placeholder="Changelog (Markdown supported)"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                />
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={() => saveDescription(firmware.id)}
                                    disabled={savingDescription}
                                    className="px-3 py-1.5 bg-primary text-on-primary rounded-full hover:bg-primary-container disabled:opacity-50 flex items-center gap-2 text-sm"
                                  >
                                    {savingDescription ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Save className="w-4 h-4" />
                                    )}
                                    Save
                                  </button>
                                  <button
                                    onClick={cancelEditDescription}
                                    disabled={savingDescription}
                                    className="px-3 py-1.5 text-on-surface-variant border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
                                  >
                                    <X className="w-4 h-4" />
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="mb-3 flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                  {firmware.description ? (
                                    <div className="max-w-none">
                                      <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={changelogMarkdownComponents}
                                      >
                                        {firmware.description}
                                      </ReactMarkdown>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-400 italic">
                                      No changelog
                                    </p>
                                  )}
                                </div>
                                <button
                                  onClick={() => startEditDescription(firmware)}
                                  aria-label="Edit changelog"
                                  className="p-1.5 text-on-surface-variant hover:bg-gray-100 rounded flex-shrink-0"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Database className="w-4 h-4" />
                                {formatFileSize(firmware.file_size)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Download className="w-4 h-4" />
                                {firmware.download_count} downloads
                              </span>
                              <span className="flex items-center gap-1">
                                <Zap className="w-4 h-4" />
                                {firmware.deployment_count} deployments
                              </span>
                              {firmware.success_rate !== null && (
                                <span className="flex items-center gap-1">
                                  <TrendingUp className="w-4 h-4" />
                                  {firmware.success_rate.toFixed(1)}% success
                                </span>
                              )}
                            </div>
                            <div className="mt-2 text-xs text-gray-400">
                              <p>MD5: {firmware.md5_checksum}</p>
                              <p>Uploaded: {formatDate(firmware.created_at)}</p>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <a
                              href={firmware.download_url}
                              download
                              className="px-3 py-2 text-on-surface-variant border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Download className="w-4 h-4" />
                              <span className="hidden sm:inline">Download</span>
                            </a>
                            <button
                              onClick={() => {
                                setSelectedFirmwareId(firmware.id);
                                setShowDeployModal(true);
                              }}
                              disabled={!firmware.is_active}
                              aria-label="Deploy firmware"
                              className="px-3 py-2 bg-primary text-on-primary rounded-full hover:bg-primary-container disabled:opacity-50 flex items-center gap-2"
                            >
                              <Zap className="w-4 h-4" />
                              <span className="hidden sm:inline">Deploy</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Deployments Tab */}
            {activeTab === "deployments" && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <h3 className="text-lg font-semibold text-on-surface">
                    Deployment History
                  </h3>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeployModal(true)}
                      className="px-4 py-2 bg-primary text-on-primary rounded-full hover:bg-primary-container flex items-center gap-2"
                    >
                      <Zap className="w-4 h-4" />
                      New Deployment
                    </button>
                    <button
                      onClick={fetchDeployments}
                      className="px-4 py-2 text-on-surface-variant border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Refresh
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="downloading">Downloading</option>
                    <option value="installing">Installing</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Filter by device MAC..."
                    value={deviceFilter}
                    onChange={(e) => setDeviceFilter(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {deployments.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Zap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">No deployments yet</p>
                    <p className="text-sm mt-2">
                      Deploy firmware to devices to see history here
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead>
                        <tr className="border-b border-gray-200">
                          {[
                            "Device",
                            "Firmware",
                            "Status",
                            "Started",
                            "Duration",
                          ].map((h) => (
                            <th
                              key={h}
                              className="text-left py-3 px-4 font-medium text-on-surface"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {deployments.map((deployment) => (
                          <tr
                            key={deployment.id}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-on-surface">
                                  {deployment.device_name || "Unnamed"}
                                </p>
                                <p className="text-sm text-gray-500 font-mono truncate max-w-[140px]">
                                  {deployment.device}
                                </p>
                                {deployment.target_type === "greenhouse" && (
                                  <span className="inline-block mt-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded">
                                    Greenhouse
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                v{deployment.firmware_version}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(deployment.status)}`}
                              >
                                {getStatusIcon(deployment.status)}
                                {deployment.status.charAt(0).toUpperCase() +
                                  deployment.status.slice(1)}
                              </span>
                              {deployment.status === "failed" &&
                                deployment.error_message && (
                                  <p
                                    className="mt-1 text-xs text-red-700 max-w-[260px]"
                                    data-testid={`deployment-failure-${deployment.id}`}
                                  >
                                    {describeOtaFailure(deployment.error_message) ||
                                      deployment.error_message}
                                    <span className="block font-mono text-gray-500">
                                      {deployment.error_message}
                                    </span>
                                  </p>
                                )}
                            </td>
                            <td className="py-3 px-4 text-sm text-on-surface-variant">
                              {formatDate(deployment.started_at)}
                            </td>
                            <td className="py-3 px-4 text-sm text-on-surface-variant">
                              {deployment.duration
                                ? `${Math.round(deployment.duration)}s`
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FirmwarePage;
